/**
 * Benvenuto animato all'apertura dell'app (direzione A "Parole che saltano", Marco 2026-09-27).
 * Parte da solo a ogni avvio; "Salta" sempre in alto; l'ultima scena resta ferma su "Cominciamo"
 * finche lei tocca. Nessuna foto e nessun audio. Il nome arriva da `display_form` (DEC-15):
 * se non l'ha ancora scelto, il saluto resta senza nome.
 */
import { useEffect, useRef, type ReactNode } from 'react'

import './intro.css'

/** Istante (ms) in cui l'ultima scena e completa. */
export const INTRO_END = 13600

function saluto(ora: number): string {
  return ora < 13 ? 'Buongiorno' : ora < 18 ? 'Buon pomeriggio' : 'Buonasera'
}

type Fx = 'pop' | 'up' | 'drop'

function F({
  t,
  fx,
  float = false,
  className = '',
  children,
}: {
  t: [number, number]
  fx: Fx
  float?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <div className={`ifx ${fx}${float ? ' float' : ''} ${className}`} data-t={`${t[0]},${t[1]}`}>
      {children}
    </div>
  )
}

export function Intro({ nome, onDone }: { nome: string | null; onDone: () => void }) {
  const stage = useRef<HTMLDivElement>(null)
  const go = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const root = stage.current
    if (!root) return
    const items = [...root.querySelectorAll<HTMLElement>('[data-t]')].map((el) => {
      const [a, b] = (el.dataset.t ?? '0,0').split(',').map(Number)
      return { el, a, b }
    })
    const start = performance.now()
    let raf = 0
    let focused = false
    const tick = (now: number) => {
      const t = now - start
      for (const { el, a, b } of items) {
        if (t >= a && t < b) {
          el.classList.add('on')
          el.classList.remove('off')
        } else if (el.classList.contains('on')) {
          el.classList.remove('on')
          el.classList.add('off')
        }
      }
      if (t >= INTRO_END && !focused) {
        focused = true
        go.current?.focus({ preventScroll: true })
      }
      if (t < INTRO_END + 500) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  const forma = (nome ?? '').trim()
  const ciao = saluto(new Date().getHours())

  return (
    <div className="intro" role="dialog" aria-modal="true" aria-label="Benvenuto in mia">
      <div className="intro-stage" ref={stage}>
        <button className="intro-skip" type="button" onClick={onDone}>
          Salta
        </button>

        <div className="intro-layer">
          <div className="intro-words intro-big">
            <F t={[200, 3000]} fx="up">
              {forma ? `${ciao},` : `${ciao}!`}
            </F>
            {forma && (
              <F t={[650, 3000]} fx="up">
                {forma}
              </F>
            )}
          </div>
          <F t={[1100, 3000]} fx="pop" float className="intro-emoji">
            🌸
          </F>
        </div>

        <div className="intro-layer">
          <F t={[3200, 5200]} fx="pop" className="intro-big">
            Sono mia.
          </F>
          <F t={[3700, 5200]} fx="pop" float className="intro-emoji">
            ✨
          </F>
        </div>

        <div className="intro-tick intro-mid">
          <F t={[5400, 6600]} fx="up">
            Le ricordo le medicine 💊
          </F>
          <F t={[6600, 7800]} fx="up">
            Tengo a mente le visite 📅
          </F>
          <F t={[7800, 9000]} fx="up">
            Rispondo alle sue domande 💬
          </F>
          <F t={[9000, 10100]} fx="up">
            Con calma, ogni giorno ☕
          </F>
        </div>

        <div className="intro-layer">
          <F t={[10200, 12400]} fx="pop" float className="intro-heart">
            ❤️
          </F>
          <F t={[10700, 12400]} fx="up" className="intro-mid">
            Sono qui per lei, ogni giorno.
          </F>
        </div>

        <div className="intro-layer">
          <F t={[12600, 999999]} fx="drop" className="intro-mid">
            mia
          </F>
          <F t={[12900, 999999]} fx="pop">
            <button ref={go} className="intro-go" type="button" onClick={onDone}>
              Cominciamo
            </button>
          </F>
        </div>
      </div>
    </div>
  )
}
